import clsx from 'clsx';
import { HeartIcon } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import useToggleMediaLike from '@/components/LikeButton/useToggleMediaLike';
import useOpenSpeedyFlow from '@/components/SpeedyFlow/hooks/useOpenSpeedyFlow';
import { cn } from '@/lib/utils';

interface VideoLikeButtonProps {
  mediaId: string;
  type: 'video' | 'photo';
  isLiked?: boolean;
  likes?: number;
}

type LabelPhase = 'hidden' | 'visible' | 'fading';

const VideoLikeButton = ({ mediaId, type, isLiked: initialIsLiked, likes }: VideoLikeButtonProps) => {
  const openSpeedyFlow = useOpenSpeedyFlow();
  const { logged, isLiked, toggle } = useToggleMediaLike({ mediaId, type, isLiked: initialIsLiked, likes });
  const [labelPhase, setLabelPhase] = useState<LabelPhase>('hidden');
  const labelTimeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearLabelTimeouts = () => {
    labelTimeouts.current.forEach(clearTimeout);
    labelTimeouts.current = [];
  };

  useEffect(() => clearLabelTimeouts, []);

  const handleClick = async (e: MouseEvent) => {
    // The card is wrapped in an <a> — never navigate or re-trigger video selection when liking.
    e.preventDefault();
    e.stopPropagation();

    if (!logged) {
      // Resume the like once they have an account. Only the toggle is replayed — the celebration
      // animation belongs to the click, and playing it minutes later would be noise.
      openSpeedyFlow({ kind: 'account', entryPoint: 'like-video' }, () => {
        toggle();
      });
      return;
    }

    const newIsLiked = await toggle();
    // Only celebrate the unliked -> liked transition: stay visible for 1s, then fade out.
    if (newIsLiked === true) {
      clearLabelTimeouts();
      setLabelPhase('visible');
      labelTimeouts.current.push(setTimeout(() => setLabelPhase('fading'), 1000));
      labelTimeouts.current.push(setTimeout(() => setLabelPhase('hidden'), 1500));
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={isLiked ? 'Remove from My Journal' : 'Add to My Journal'}
      aria-pressed={isLiked}
      data-component="like-button"
      data-liked={isLiked ? 'true' : 'false'}
      className="preflight preflight-button relative flex items-center justify-center"
    >
      <HeartIcon className={clsx('size-8 text-white', isLiked && 'fill-current')} />
      {labelPhase !== 'hidden' && (
        // Centering transform lives on this static wrapper so the fade-out animation (which
        // resets transform) doesn't drift the label sideways — the inner span animates opacity only.
        <span className="pointer-events-none absolute left-1/2 top-full -mt-0.5 -translate-x-1/2">
          <span
            className={cn(
              'whitespace-nowrap text-xs font-semibold text-white',
              labelPhase === 'fading' && 'duration-500 animate-out fade-out fill-mode-forwards',
            )}
          >
            Added to My Journal!
          </span>
        </span>
      )}
    </button>
  );
};

export default VideoLikeButton;
